import React from "react";
import { type ExtensionFieldValueObjectType } from "~/graphql/supergraphTypes";
import { EmptyValueRenderer } from "./EmptyValueRenderer";
import * as styles from "./styles_vanilla.css";

/**
 * Format a number as a percentage with proper locale formatting and comma separation
 * Consolidates percentage formatting logic to match existing utility patterns
 * @param value - The numeric value to format
 * @param precision - Number of decimal places (default: 2)
 * @param trimTrailingZeros - Whether to remove trailing zeros (default: true)
 * @returns Formatted percentage string with comma separators for large numbers
 */
const formatPercentage = (
  value: number,
  precision: number = 2,
  trimTrailingZeros: boolean = true,
): string => {
  const negative = value < 0;
  const positiveValue = Math.abs(value);

  // Format with specified precision
  const formattedNumber = positiveValue.toLocaleString(undefined, {
    minimumFractionDigits: trimTrailingZeros ? 0 : precision,
    maximumFractionDigits: precision,
  });
  const result = `${formattedNumber}%`;
  return negative ? `(${result})` : result;
};

interface PercentileFieldRendererProps {
  value: ExtensionFieldValueObjectType;
}

/**
 * Cell renderer for percentile extension fields
 * Displays percentile values with proper percentage formatting using consolidated utility
 * Matches the behavior of twoDigitPercentFormat for consistency across the application
 */
export const PercentileFieldRenderer: React.FC<PercentileFieldRendererProps> = ({ value }) => {
  const percentileValue = value.floatValue;

  if (percentileValue === null || percentileValue === undefined) {
    return <EmptyValueRenderer />;
  }

  // Format as percentage with 2 decimal places and trailing zero trimming
  // (assuming the value is already in percentage form, e.g., 85.5 for 85.5%)
  const formattedValue = formatPercentage(percentileValue, 2, true);

  return (
    <span className={styles.percentileValue} title={formattedValue}>
      {formattedValue}
    </span>
  );
};
